/**
 * The recorded guzheng (lane C0-FRONT-AUDIO, docs/research/GUZHENG_SOUND.md): one open-string pluck
 * of each string from D2 to A5 of a real 21-string zheng tuned D F G A C, cut from a CC0 recording
 * (public/audio/guzheng/SOURCES.md) by scripts/lanes/c0-front-audio/cut_guzheng.py, the tails past
 * the next pluck continued with each string's own partials. All in one small MP3 (about 0.8 MB),
 * fetched once through the asset cache when the music starts; until it is in, and if it fails, the
 * zheng is synthesized as before (samples.ts).
 *
 * The bank plays the nearest string and bends it to the note (playback rate), which is how a real
 * zheng gets most of its notes too: an open string, pressed (按) up by a step or so.
 */
import { getAsset } from '../cache/assets';

/** One string in the sprite: its pitch, how far the recording sits from it, and where it is. */
interface StringCut {
  readonly midi: number;
  /** The recorded string's tuning against equal temperament at A 440, cents. */
  readonly cents: number;
  /** Seconds into the sprite. */
  readonly start: number;
  readonly secs: number;
}

const FILE = 'audio/guzheng/guzheng.mp3';
const RATE = 32000;

// From cut_guzheng.py's guzheng.json.
const STRINGS: readonly StringCut[] = [
  { midi: 38, cents: 17.3, start: 0.0, secs: 6.5 },
  { midi: 41, cents: 15.0, start: 6.75, secs: 6.5 },
  { midi: 43, cents: -7.6, start: 13.5, secs: 6.5 },
  { midi: 45, cents: 10.6, start: 20.25, secs: 6.2271 },
  { midi: 48, cents: 9.5, start: 26.7271, secs: 5.76 },
  { midi: 50, cents: 12.1, start: 32.7371, secs: 5.4682 },
  { midi: 53, cents: 14.9, start: 38.4553, secs: 5.058 },
  { midi: 55, cents: 7.7, start: 43.7633, secs: 4.8018 },
  { midi: 57, cents: 13.0, start: 48.8151, secs: 4.76 },
  { midi: 60, cents: 8.4, start: 53.8251, secs: 4.2165 },
  { midi: 62, cents: -0.9, start: 58.2916, secs: 4.003 },
  { midi: 65, cents: -3.9, start: 62.5446, secs: 3.7027 },
  { midi: 67, cents: 1.0, start: 66.4972, secs: 3.5151 },
  { midi: 69, cents: 1.4, start: 70.2623, secs: 3.337 },
  { midi: 72, cents: 5.4, start: 73.8493, secs: 3.0867 },
  { midi: 74, cents: 10.3, start: 77.1861, secs: 2.9303 },
  { midi: 81, cents: 8.3, start: 80.3664, secs: 2.6 },
];

/** A decoded string: its samples at `RATE`, and the playback rate that brings it to its own pitch. */
export interface RecordedString {
  readonly midi: number;
  readonly data: Float32Array;
  readonly sampleRate: number;
  /** Semitones to add to (midi − this.midi) to land in tune. */
  readonly tune: number;
}

let loading: Promise<readonly RecordedString[] | null> | null = null;
let ready: readonly RecordedString[] | null = null;

/** The strings once decoded, else null (still loading, failed, or never asked for). */
export const recordedZheng = (): readonly RecordedString[] | null => ready;

/** Starts (once) and awaits the download and decoding; null if it fails, and the synth stays. */
export function loadRecordedZheng(): Promise<readonly RecordedString[] | null> {
  return (loading ??= (async () => {
    try {
      const { blob } = await getAsset(FILE);
      const ctx = new OfflineAudioContext(1, 1, RATE);
      const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
      const all = buf.getChannelData(0);
      const sr = buf.sampleRate;
      ready = STRINGS.map((s) => {
        // The MP3's encoder delay shifts everything a little: start at the pluck itself.
        const from = Math.max(0, Math.floor((s.start - 0.05) * sr));
        const to = Math.min(all.length, Math.floor((s.start + 0.12) * sr));
        let peak = 0;
        for (let i = from; i < to; i++) peak = Math.max(peak, Math.abs(all[i]!));
        let at = from;
        while (at < to && Math.abs(all[at]!) < peak * 0.08) at++;
        at = Math.max(from, at - Math.floor(0.003 * sr));
        const data = all.slice(at, Math.min(all.length, at + Math.floor(s.secs * sr)));
        // A short fade in over the first samples taken before the pluck.
        const fade = Math.min(data.length, Math.floor(0.002 * sr));
        for (let i = 0; i < fade; i++) data[i]! *= i / fade;
        return { midi: s.midi, data, sampleRate: sr, tune: -s.cents / 100 };
      });
      return ready;
    } catch (error) {
      console.warn('Recorded guzheng not loaded; the zheng stays synthesized', error);
      return null;
    }
  })());
}

/** The string a note is played on: the nearest one, preferring the string below (pressed up). */
export function stringFor(set: readonly RecordedString[], midi: number): RecordedString {
  let best = set[0]!;
  let cost = Infinity;
  for (const s of set) {
    const d = midi - s.midi;
    // Pressing up is natural; reaching down costs a little more.
    const c = d >= 0 ? d : -d * 1.25;
    if (c < cost) {
      cost = c;
      best = s;
    }
  }
  return best;
}
